/**
 * The alignment vocabularies. Four spellings live side by side: the legacy
 * `L | M | R` the block and the widgets still store, the legacy vertical
 * `T | M | B`, and the words v0 spells both axes in.
 *
 * They do not overlap, so one CSS map covers all of them — which is the point:
 * handing a map the "wrong" vocabulary used to yield an empty variable (a row's
 * `middle` once left `align-items` unset and let the widgets stretch).
 */
export type HorizontalPosition = 'L' | 'M' | 'R';
/** The column's legacy vertical letters, `T | M | B`. */
export type VerticalPosition = 'T' | 'M' | 'B';
export type HorizontalAlign = 'left' | 'center' | 'right';
export type VerticalAlign = 'top' | 'middle' | 'bottom';

export type Alignment = HorizontalPosition | VerticalPosition | HorizontalAlign | VerticalAlign;

/** Legacy letter → v0 word. `M` is `middle` on the vertical axis and `center` on the horizontal one. */
export const VERTICAL_ALIGN_BY_POSITION: Readonly<Record<VerticalPosition, VerticalAlign>> = {
    T: 'top',
    M: 'middle',
    B: 'bottom',
};

export const HORIZONTAL_ALIGN_BY_POSITION: Readonly<Record<HorizontalPosition, HorizontalAlign>> = {
    L: 'left',
    M: 'center',
    R: 'right',
};

function invert<K extends string, V extends string>(map: Readonly<Record<K, V>>): Readonly<Record<V, K>> {
    return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key])) as Record<V, K>;
}

const VERTICAL_POSITION_BY_ALIGN = invert(VERTICAL_ALIGN_BY_POSITION);
const HORIZONTAL_POSITION_BY_ALIGN = invert(HORIZONTAL_ALIGN_BY_POSITION);

/**
 * v0 word → the letter `sp-radio-group` selects and emits. The panels bind that control, so a v0
 * value has to cross back into its vocabulary on the way in and out.
 */
export function toVerticalPosition(align: VerticalAlign | null | undefined): VerticalPosition | null {
    return align ? (VERTICAL_POSITION_BY_ALIGN[align] ?? null) : null;
}

export function toHorizontalPosition(align: HorizontalAlign | null | undefined): HorizontalPosition | null {
    return align ? (HORIZONTAL_POSITION_BY_ALIGN[align] ?? null) : null;
}
